import React from 'react';
import { Button, Icon, Message, Modal, Segment, Table } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import EventService from 'src/services/api/eventService';
import { dayInTZ, EVENT_DATETIME_FORMAT, eventDisplayName } from 'src/components/helpers';

const DetailTable = ({ title, rows, columns }) => {
  if (!rows?.length) return null;

  return (
    <Segment>
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      <Table celled compact size="small" striped>
        <Table.Header>
          <Table.Row>
            {columns.map((col) => (
              <Table.HeaderCell key={col.key}>{col.label}</Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((row, index) => (
            <Table.Row key={`${row.row}-${index}`}>
              {columns.map((col) => (
                <Table.Cell key={col.key}>{col.render(row)}</Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Segment>
  );
};

const participantLabel = (row) =>
  row.participant || `${row.name || ''} ${row.lastName || ''}`.trim() || '—';

const notAddedColumns = [
  { key: 'row', label: 'Fila', render: (r) => r.row },
  { key: 'id', label: 'ID reserva', render: (r) => r.audGuest_id || '—' },
  { key: 'name', label: 'Participante', render: participantLabel },
  { key: 'identification', label: 'Cédula', render: (r) => r.identification || '—' },
  { key: 'reason', label: 'Motivo', render: (r) => r.reason || '—' },
];

const UploadResults = ({ result }) => {
  const { message, summary = {}, created = [], omitted = [], errors = [] } = result;
  const notCreated = result.notCreated || errors;
  const omittedCount = summary.omitted ?? omitted.length;
  const notCreatedCount = summary.notCreated ?? notCreated.length;
  const hasCreated = created.length > 0;

  return (
    <>
      <Message success={hasCreated} warning={!hasCreated}>
        <Message.Header>
          {hasCreated ? 'Carga masiva completada' : 'Ninguna reserva creada'}
        </Message.Header>
        {message && <p style={{ margin: '8px 0 0 0' }}>{message}</p>}
        <Message.List>
          <Message.Item>
            <strong>Filas procesadas:</strong> {summary.totalRows ?? 0}
          </Message.Item>
          <Message.Item>
            <strong>Reservas creadas:</strong> {summary.created ?? created.length}
          </Message.Item>
          <Message.Item>
            <strong>Omitidas:</strong> {omittedCount}
          </Message.Item>
          <Message.Item>
            <strong>No creadas:</strong> {notCreatedCount}
          </Message.Item>
        </Message.List>
      </Message>

      <DetailTable
        title="Reservas creadas"
        rows={created}
        columns={[
          { key: 'row', label: 'Fila', render: (r) => r.row },
          { key: 'id', label: 'ID reserva', render: (r) => r.audGuest_id },
          { key: 'name', label: 'Participante', render: participantLabel },
          { key: 'identification', label: 'Cédula', render: (r) => r.identification },
        ]}
      />

      <DetailTable title="Omitidas" rows={omitted} columns={notAddedColumns} />

      <DetailTable title="No creadas" rows={notCreated} columns={notAddedColumns} />
    </>
  );
};

const BulkUploadServiceParticipantsModal = ({ clickedEvent, getEvent }) => {
  const [open, setOpen] = React.useState(false);
  const [file, setFile] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const resetForm = () => {
    setFile(null);
    setResult(null);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setResult(null);
  };

  const handleUpload = () => {
    if (!file) {
      return Swal.fire('Selecciona un archivo Excel (.xlsx) para cargar.');
    }

    if (!clickedEvent?.id || !clickedEvent?.eventSlugname) {
      return Swal.fire('No se encontró el evento seleccionado.');
    }

    setLoading(true);
    EventService.bulkUploadServiceParticipants({
      file,
      audEvent_id: clickedEvent.id,
      eventSlugname: clickedEvent.eventSlugname,
    })
      .then((response) => {
        if (response.data.success) {
          setResult(response.data);
          getEvent(clickedEvent.id, clickedEvent.eventSlugname);
        } else if (response.data.message) {
          Swal.fire(response.data.message);
        }
      })
      .catch((error) => {
        const message = error.response?.data?.message || 'Error al cargar el archivo.';
        Swal.fire(message);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Button icon labelPosition="left" onClick={() => setOpen(true)}>
        <Icon name="upload" />
        Carga masiva Excel
      </Button>
      <Modal open={open} onClose={handleClose} size="large" closeIcon>
        <Modal.Header>Carga masiva de asistentes</Modal.Header>
        <Modal.Content scrolling>
          {clickedEvent?.eventSlugname && (
            <p style={{ marginTop: 0 }}>
              <strong>Evento:</strong> {eventDisplayName(clickedEvent.eventSlugname)}
              {clickedEvent?.date && <> — {dayInTZ(clickedEvent.date, EVENT_DATETIME_FORMAT)}</>}
            </p>
          )}

          {!result && (
            <>
              <Message info>
                <p style={{ margin: '0 0 8px 0' }}>
                  Sube un Excel con las mismas columnas del reporte de asistencia del servicio.
                </p>
                <p style={{ margin: 0 }}>
                  Para crear reservas nuevas, deja vacía la columna <strong>ID de Reserva</strong> e
                  incluye <strong>Identificación</strong>, <strong>Nombre</strong>,{' '}
                  <strong>Apellido</strong>, <strong>Correo Electrónico</strong>,{' '}
                  <strong>Teléfono</strong> y <strong>Género</strong>.
                </p>
              </Message>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ marginBottom: '12px' }}
              />
              {file && (
                <p style={{ margin: 0 }}>
                  <Icon name="file excel outline" />
                  {file.name}
                </p>
              )}
            </>
          )}

          {result && <UploadResults result={result} />}
        </Modal.Content>
        <Modal.Actions>
          {result ? (
            <>
              <Button onClick={resetForm}>Cargar otro archivo</Button>
              <Button primary onClick={handleClose}>
                Cerrar
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose}>Cancelar</Button>
              <Button primary loading={loading} disabled={loading || !file} onClick={handleUpload}>
                Subir y procesar
              </Button>
            </>
          )}
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default BulkUploadServiceParticipantsModal;
